/**
 * Inline article CTA.
 *
 * Expands empty placeholders typed into a blog article body into the designed
 * CTA card:
 *
 *   <div class="cta"></div>
 *   <div class="cta" data-product="sport" data-heading="Save 30% on Sport"></div>
 *
 * Defaults, and the product title/url/featured image for each handle, come from
 * theme settings via #sth-article-cta-config. Any data-* attribute on the
 * placeholder overrides the matching default for that one placement.
 *
 * Supported attributes: data-variant ("full" or "simple"), data-product,
 * data-heading, data-text, data-bullets (separated by |), data-button,
 * data-url, data-image (pass an empty value to drop the image), data-params.
 *
 * The "simple" variant drops the product image and the bullets. It renders a
 * centred subtitle only when the placeholder passes data-text; the supporting
 * text from theme settings is left to the full variant.
 */
(function () {
  'use strict';

  var READY_ATTR = 'data-sth-cta-ready';
  var SELECTOR = 'div.cta, div.sth-cta, div[data-sth-cta]';

  var defaults = {
    heading: '',
    text: '',
    button: 'Shop now',
    url: '/products/release',
    product: '',
    params: '',
    bullets: '',
    variant: 'full',
    products: {},
  };

  var config = readConfig();

  function readConfig() {
    var node = document.getElementById('sth-article-cta-config');
    if (!node) return defaults;

    try {
      return Object.assign({}, defaults, JSON.parse(node.textContent));
    } catch (error) {
      return defaults;
    }
  }

  /** release -> Release, the-protocols -> The Protocols */
  function titleize(handle) {
    return handle
      .split('-')
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  /** A placeholder we wrote ourselves, not some other div that happens to be .cta. */
  function isPlaceholder(element) {
    if (element.hasAttribute(READY_ATTR)) return false;
    if (element.hasAttribute('data-sth-cta')) return true;
    return element.children.length === 0 && element.textContent.trim() === '';
  }

  function buildUrl(element, handle, product) {
    var url = element.getAttribute('data-url');
    if (!url) url = product ? product.url : '';
    if (!url) url = handle ? '/products/' + handle : config.url;

    var params = element.getAttribute('data-params');
    if (params === null) params = config.params;
    if (!params) return url;

    return url + (url.indexOf('?') === -1 ? '?' : '&') + params.replace(/^[?&]/, '');
  }

  function expand(element) {
    var variant = (element.getAttribute('data-variant') || config.variant || 'full').toLowerCase();
    var isSimple = variant === 'simple';
    var handle = element.getAttribute('data-product') || config.product || '';
    var product = (config.products && config.products[handle]) || null;
    var productName = product ? product.title : handle ? titleize(handle) : '';

    var fill = function (value) {
      return (value || '').replace(/\[product\]/g, productName);
    };

    var heading = fill(element.getAttribute('data-heading') || config.heading);
    // The simple variant's subtitle is opt-in: it comes from the placeholder,
    // never from the theme setting that fills the full variant.
    var textSource = isSimple
      ? element.getAttribute('data-text')
      : element.getAttribute('data-text') || config.text;
    var text = fill(textSource);
    var label = fill(element.getAttribute('data-button') || config.button);

    if (!label) return;

    var imageAttribute = element.getAttribute('data-image');
    var image = imageAttribute !== null ? imageAttribute : product ? product.image : '';
    if (isSimple) image = '';

    element.classList.add('sth-article-cta');
    element.classList.add('sth-article-cta--' + variant);
    element.setAttribute(READY_ATTR, '');
    element.textContent = '';

    if (image) {
      var media = document.createElement('div');
      media.className = 'sth-article-cta__media';

      var img = document.createElement('img');
      img.src = image;
      img.alt = productName;
      img.loading = 'lazy';
      media.appendChild(img);

      element.appendChild(media);
    }

    var inner = document.createElement('div');
    inner.className = 'sth-article-cta__inner';

    if (heading) {
      var headingEl = document.createElement('div');
      headingEl.className = 'sth-article-cta__heading';
      headingEl.textContent = heading;
      inner.appendChild(headingEl);
    }

    if (text) {
      var textEl = document.createElement('div');
      textEl.className = isSimple
        ? 'sth-article-cta__text subtitle-s2'
        : 'sth-article-cta__text';
      textEl.textContent = text;
      inner.appendChild(textEl);
    }

    var bullets = (isSimple ? '' : element.getAttribute('data-bullets') || config.bullets || '')
      .split(/[|\n]/)
      .map(function (item) {
        return fill(item).trim();
      })
      .filter(Boolean);

    if (bullets.length) {
      var list = document.createElement('ul');
      list.className = 'sth-article-cta__list';
      bullets.forEach(function (item) {
        var listItem = document.createElement('li');
        listItem.textContent = item;
        list.appendChild(listItem);
      });
      inner.appendChild(list);
    }

    var link = document.createElement('a');
    link.className = 'sth-article-cta__button button custom-stemregenhorizon-button-large';
    link.href = buildUrl(element, handle, product);
    link.textContent = label;
    if (handle) link.setAttribute('data-sth-cta-product', handle);
    inner.appendChild(link);

    element.appendChild(inner);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SELECTOR).forEach(function (element) {
      if (isPlaceholder(element)) expand(element);
    });
  }

  function start() {
    scan(document);

    // Article bodies also arrive after load — the blog content modal and
    // section rendering both replace markup client-side.
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        scan(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
